import { useThrottle } from '@react-hook/throttle';
import { useQuery } from '@tanstack/react-query';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { apiClient } from 'api/ApiClient';
import { type GetMembersParams, InviteStatus } from 'api/types';
import { useAuth } from 'shared/lib/auth/useAuth';

import { QueryKeys } from './QueryKeys';

export const useGetMembers = (params: GetMembersParams, enabled = true) => {
  const [throttledParams, setThrottledParams] = useThrottle(params, 3);

  useDeepCompareEffect(() => {
    setThrottledParams(params);
  }, [params]);

  return useQuery({
    queryKey: [QueryKeys.GetMembers, throttledParams],
    queryFn: () => apiClient.getMembers(throttledParams),
    enabled,
  });
};

export const useGetMembersWaitingForApprove = () => {
  const { permissions } = useAuth();
  const enabled = permissions.has('can_invite_users') && permissions.has('can_view_company_settings');

  return useQuery({
    queryKey: [QueryKeys.GetMembers, { invite_status: InviteStatus.WAITING_FOR_APPROVAL }],
    queryFn: () => apiClient.getMembers({ invite_status: InviteStatus.WAITING_FOR_APPROVAL }),
    select: (data) => data.count,
    enabled,
  });
};
